import { useMemo, useCallback } from "react";
import {
  compressImageWithCanvas,
  base64Size,
  compressBase64ToSize,
} from "../../../shared/utils/imageHelpers";
import { MAX_CAPTURE_SIZE_BYTES, COMPRESS_IMAGE_FILE } from "../constants";
import { fileToDataUrl, fileHasAllowedExtension } from "../utils";
import { getNotificationService } from "../services/notification";

/**
 * Image processing pipeline: validation (by extension), compression, file→dataUrl.
 * Notifications are delegated to the injected service.
 * @param {Object} options
 * @param {string} options.inputFileType - e.g. "png,jpeg,jpg"
 * @param {number} options.maxFileSize - MB
 * @param {number|null} options.maxSelect
 * @param {import("../services/notification").NotificationService} [options.notificationService]
 */
export function useImageProcessing({ inputFileType, maxFileSize, maxSelect, notificationService }) {
  const notify = getNotificationService(notificationService);
  const allowedExtensions = useMemo(
    () => inputFileType.split(",").map((ext) => `.${ext.trim()}`),
    [inputFileType]
  );
  const maxBytes = maxFileSize * 1024 * 1024;

  const acceptAttr = useMemo(
    () =>
      inputFileType
        .split(",")
        .map((ext) => `.${ext.trim()}`)
        .join(","),
    [inputFileType]
  );

  const validateSingleFile = useCallback(
    (file) => {
      if (!fileHasAllowedExtension(file, allowedExtensions)) {
        notify.warn(
          "Invalid file type",
          `Only ${inputFileType.replaceAll(",", ", ")} files are allowed.`
        );
        return false;
      }
      if (file.size > maxBytes) {
        notify.warn("File too large", `File must be less than ${maxFileSize}MB.`);
        return false;
      }
      return true;
    },
    [allowedExtensions, maxBytes, maxFileSize, inputFileType, notify]
  );

  const validateGalleryFiles = useCallback(
    (selectedFiles, currentImageCount) => {
      const invalid = selectedFiles.filter(
        (file) => !fileHasAllowedExtension(file, allowedExtensions)
      );
      const tooLarge = selectedFiles.filter((file) => file.size > maxBytes);
      if (invalid.length > 0) {
        notify.warn(
          "Invalid file type",
          `Only ${inputFileType.replaceAll(",", ", ")} files are allowed.`
        );
        return false;
      }
      if (tooLarge.length > 0) {
        notify.warn("File too large", `Each file must be less than ${maxFileSize}MB.`);
        return false;
      }
      const remaining = maxSelect != null ? maxSelect - currentImageCount : selectedFiles.length;
      if (maxSelect != null && selectedFiles.length > remaining) {
        notify.warn("Too many files", `You can select up to ${remaining} files only.`);
        return false;
      }
      return true;
    },
    [allowedExtensions, maxBytes, maxFileSize, maxSelect, inputFileType, notify]
  );

  const processCapturedBuffer = useCallback(async (imageBuffer) => {
    let buffer = imageBuffer;
    const sizeInBytes = base64Size(imageBuffer);
    if (sizeInBytes > MAX_CAPTURE_SIZE_BYTES) {
      buffer = await compressBase64ToSize(buffer, MAX_CAPTURE_SIZE_BYTES);
    }
    return buffer;
  }, []);

  const processFileForReupload = useCallback(
    async (file) => {
      if (!validateSingleFile(file)) return null;
      let processed = file;
      if (COMPRESS_IMAGE_FILE && file.type.startsWith("image/")) {
        processed = await compressImageWithCanvas(file);
      }
      return fileToDataUrl(processed);
    },
    [validateSingleFile]
  );

  const processFilesForGallery = useCallback(async (files) => {
    let list = Array.from(files);
    if (COMPRESS_IMAGE_FILE && list.length > 0) {
      list = await Promise.all(
        list.map((file) => (file.type.startsWith("image/") ? compressImageWithCanvas(file) : file))
      );
    }
    return Promise.all(list.map((file) => fileToDataUrl(file)));
  }, []);

  return {
    acceptAttr,
    validateSingleFile,
    validateGalleryFiles,
    processCapturedBuffer,
    processFileForReupload,
    processFilesForGallery,
    notifyCaptureFailed: () => notify.warn("Capture failed", "Please retry."),
  };
}
